// MCO stress-test grid — the primary MCO interaction, and the only entry point to it. Lives
// INSIDE the Scenario page's Stress Test section, in the same visual structure as the RP/SCO
// per-plan grids: same price/yield axes, an input-cost control in $/acre, and one "MCO Detail…"
// button for the deeper analysis one level down (Calculator, Input Cost Tracker, ECO/MCO
// Scenario Comparison, Historical Backtest — see McoDetailsDialog).

import { useEffect, useMemo, useState } from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { stressedHarvestCosts } from "api-toolkit/mco/mcoStressGrid";
import { useMco } from "api-toolkit/mco/useMco";
import { ArrowDownIcon } from "components/Icons/Icons";
import { formatPrice } from "utility/formatters";

import { buildStressTestSpans } from "../useInsuranceProjectedPayment";
import McoDetailsDialog from "./McoDetailsDialog";
import McoStressTestTable from "./McoStressTestTable";

const round2 = (v) => Math.round(Number(v) * 100) / 100;

export default function McoSection({
  mcoPlans,
  basePlan,
  farmParameters,
  geo,
  countyData = null,
  stressTestParameters = null,
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Rogers: the input-cost control is expressed in $/acre, not a multiplier. Empty = baseline.
  const [costText, setCostText] = useState("");
  // Baseline $/ac from the Official actuarial row, captured once loaded (state, not a
  // derived value, so costMult below can feed useMco on the same render pass).
  const [baseCosts, setBaseCosts] = useState(null);
  const costMult = baseCosts > 0 && costText !== "" ? Number(costText) / baseCosts : 1;

  const mcoPlan = mcoPlans?.[0];

  // Share the exact axes of the sibling RP/SCO grids so the MCO benefit is read against the
  // same price/yield conditions; without county data the grid falls back to its own EAY/PP sweeps.
  const gridAxes = useMemo(() => {
    if (!countyData) return null;
    const { county_yield_span, price_span } = buildStressTestSpans({
      countyParameters: countyData,
      approved_yield: farmParameters?.approved_yield,
      testParameters: stressTestParameters,
    });
    if (!county_yield_span?.length || !price_span?.length) return null;
    return { yieldSteps: county_yield_span, priceSteps: price_span };
  }, [countyData, farmParameters?.approved_yield, stressTestParameters]);

  const mco = useMco({
    geo,
    mcoPlan,
    farm: {
      approvedYield: farmParameters?.approved_yield,
      acres: farmParameters?.reported_acreage,
      sharePct: farmParameters?.share_percent,
    },
    underlyingCoverage: basePlan?.coverage_level_percent || 0.75,
    underlyingPremiumPerAcre: basePlan?.producer_premium_amount_per_acre,
    underlyingLiabilityPerAcre: basePlan?.liability_amount_per_acre,
    costMult,
    gridAxes,
  });

  useEffect(() => {
    setBaseCosts(mco.stressBaseline ? stressedHarvestCosts(mco.stressBaseline, 1) : null);
  }, [mco.stressBaseline]);

  if (!mcoPlan) return null;

  const body = (
    <>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
        <TextField
          label="Input costs $/ac"
          type="number"
          size="small"
          value={costText !== "" ? costText : baseCosts != null ? round2(baseCosts) : ""}
          onChange={(e) => setCostText(e.target.value)}
          inputProps={{ min: 0, step: 5 }}
          sx={{ width: 170 }}
          helperText={baseCosts != null ? `expected ${formatPrice(baseCosts, 2)}/ac` : undefined}
        />
        <Button variant="outlined" onClick={() => setDetailsOpen(true)}>
          MCO Detail…
        </Button>
      </Stack>

      {!mco.isAvailable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Could not load MCO data
        </Alert>
      )}

      <McoStressTestTable mco={mco} />

      <McoDetailsDialog open={detailsOpen} onClose={() => setDetailsOpen(false)} mco={mco} geo={geo} />
    </>
  );

  return (
    <Accordion
      disableGutters
      sx={{
        backgroundColor: "background.paper",
        "&.Mui-expanded": {
          backgroundColor: "grey.100",
        },
        "&:before": {
          display: "none",
        },
        border: "none",
        borderRadius: "0.875rem !important",
        boxShadow: "none",
        mb: 2,
      }}
    >
      <AccordionSummary
        sx={{
          padding: 3,
          flexDirection: "row-reverse",
          "& .MuiAccordionSummary-content": {
            margin: "0 0 0 1rem",
          },
        }}
        expandIcon={
          <ArrowDownIcon
            sx={{
              width: 48,
              height: 48,
              color: "primary.main",
              opacity: 0.8,
              "&:hover": {
                opacity: 1,
                color: "primary.dark",
              },
            }}
          />
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}>
          <Typography
            sx={{ fontSize: "1.25rem !important", fontWeight: "600 !important", mb: "0 !important" }}
            variant="h5"
          >
            {mcoPlan.insurance_plan_name || "Margin Coverage Option (MCO)"}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ maxWidth: "1050px" }}>{body}</AccordionDetails>
    </Accordion>
  );
}
